import {
  CAPSIZE_SPLASH_COUNT,
  FOAM_DRIFT_PX_S,
  HULL_CHOP_FOAM_GAIN,
  SLAM_CLOSING_PX_S,
  SLAM_GAP_PX,
  SPINDRIFT_GRAVITY,
  SPRAY_GRAVITY,
} from '../constants';
import { hash, smoothstep } from '../math';

interface FoamPatch {
  x: number;
  /** Offset below the surface, px. Re-seated against the live surface each frame. */
  dy: number;
  radius: number;
  life: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

/** A breaking crest: `[x, y, localSteepness]`. */
type Crest = [number, number, number];

export interface FoamFrame {
  daylight: number;
  dt: number;
  /** Wall-clock seconds. Seeds the deterministic churn jitter. */
  timeS: number;
  reducedMotion: boolean;
  /** Whitecap intensity from the meteorological model, 0 to 1. */
  whitecapIntensity: number;
  /** Wind speed, m/s. Above about F6 the wind tears spray off the crests. */
  windSpeedMps: number;
  surfaceYAt: (x: number) => number;
  /** Quality caps for this level. */
  foamMax: number;
  spindriftMax: number;
  particleMax: number;
}

export interface HullChurnFrame extends FoamFrame {
  centreX: number;
  halfWidthPx: number;
  pitchRad: number;
  heavePx: number;
  heaveRatePxS: number;
  pitchRateRadS: number;
  /** Significant height in px, the sea-energy term in the churn. */
  significantPx: number;
  /** Effective draw-time inland judder, after capsize/reduced-motion gating. */
  judderChop: number;
}

export interface SprayFrame extends FoamFrame {
  centreX: number;
  baseY: number;
  hullPx: number;
  pitchRad: number;
  heavePx: number;
  capsize: number;
  previousCapsize: number;
  /** Bow clearance last frame, px. */
  previousGapPx: number;
  elevationAt: (x: number) => number;
  clampPx: number;
}

/**
 * Everything white on the water: hull churn, whitecap foam, spindrift and slam
 * spray.
 *
 * All four are drawn as round dabs batched into alpha buckets — one path and one
 * fill per bucket rather than a fill per particle — which is what keeps a heavy
 * sea affordable on a low-end device.
 */
export class FoamRenderer {
  /** Whitecap foam patches, shed by breaking crests and by the hull's wash. */
  private readonly foam: FoamPatch[] = [];

  /** Spray torn off crests by a strong wind and blown downwind. */
  private readonly spindrift: Particle[] = [];

  /** Slam spray and the capsize splash burst. */
  private readonly particles: Particle[] = [];

  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  /**
   * The boat displaces water, so the two points where the hull crosses the
   * surface carry a small churn that grows with sea energy and hull motion.
   *
   * Drawn as a few jittered dabs per end (deterministic, re-jittered about seven
   * times a second), plus the occasional ordinary foam patch shed aft so the
   * churn trails off as wash. This is what seats the boat *in* the water rather
   * than on it.
   */
  drawHullChurn(frame: HullChurnFrame): void {
    const { ctx } = this;
    const {
      centreX,
      halfWidthPx,
      pitchRad,
      significantPx,
      heaveRatePxS,
      pitchRateRadS,
      judderChop,
      daylight,
      timeS,
      reducedMotion,
      surfaceYAt,
      foamMax,
    } = frame;

    const cosPitch = Math.cos(pitchRad);
    const bowX = centreX + cosPitch * halfWidthPx * 0.92;
    const sternX = centreX - cosPitch * halfWidthPx * 0.92;

    const churn = Math.min(
      1,
      Math.max(0, (significantPx - 3) / 34) +
        Math.abs(heaveRatePxS) * 0.012 +
        Math.abs(pitchRateRadS) * 0.5 +
        judderChop * HULL_CHOP_FOAM_GAIN,
    );

    if (churn <= 0.06) {
      return;
    }

    const seed = reducedMotion ? 0 : timeS;

    ctx.fillStyle = `rgba(255,255,255,${(0.3 * (0.4 + 0.6 * daylight) * churn).toFixed(3)})`;
    ctx.beginPath();

    for (let end = 0; end < 2; end += 1) {
      const x = end ? sternX : bowX;
      const y = surfaceYAt(x);

      for (let j = 0; j < 3; j += 1) {
        const r1 = hash(end * 9.7 + j * 3.1 + Math.floor(seed * 7));
        const r2 = hash(end * 5.3 + j * 7.9 + Math.floor(seed * 7));
        const radius = (1.6 + 2.6 * churn) * (0.5 + 0.5 * r2);

        ctx.moveTo(x + radius * 2, y);
        ctx.ellipse(
          x + (r1 - 0.5) * 9,
          y + r2 * 2.2,
          radius * 2,
          radius * 0.7,
          0,
          0,
          Math.PI * 2,
        );
      }
    }

    ctx.fill();

    if (
      !reducedMotion &&
      Math.random() < 0.3 * churn &&
      this.foam.length < foamMax
    ) {
      this.foam.push({
        x: sternX - 2,
        dy: Math.random() * 2.5,
        radius: 0.8 + Math.random() * 1.6,
        life: 0.25 + Math.random() * 0.3,
      });
    }
  }

  /**
   * Whitecapping: the wind and steepness criteria decide *how much* foam there
   * is, and the on-screen crest slope only biases *which* crests break first. A
   * glassy, low-wind sea shows none — the foam is the criteria firing.
   */
  drawWhitecaps(
    frame: FoamFrame,
    surfacePoints: ReadonlyArray<[number, number]>,
  ): void {
    const crests = FoamRenderer.findCrests(surfacePoints);

    if (frame.reducedMotion) {
      this.drawStaticWhitecaps(frame, crests);

      return;
    }

    this.drawAnimatedWhitecaps(frame, crests);
    this.drawSpindrift(frame, crests);
  }

  /** Local maxima of the surface polyline, with their local slope. */
  private static findCrests(points: ReadonlyArray<[number, number]>): Crest[] {
    const crests: Crest[] = [];

    for (let i = 2; i < points.length - 2; i += 1) {
      if (points[i - 1][1] > points[i][1] && points[i + 1][1] > points[i][1]) {
        const slope = Math.abs(
          (points[i + 2][1] - points[i - 2][1]) /
            (points[i + 2][0] - points[i - 2][0]),
        );

        crests.push([points[i][0], points[i][1], Math.min(1, slope / 0.7)]);
      }
    }

    return crests;
  }

  /** Reduced motion: deterministic, hash-seeded dabs so nothing flickers. */
  private drawStaticWhitecaps(
    { whitecapIntensity, daylight }: FoamFrame,
    crests: Crest[],
  ): void {
    const { ctx } = this;

    crests.forEach(([x, y, localSteepness], c) => {
      const count = Math.round(
        whitecapIntensity * 5 * (0.4 + 0.6 * localSteepness),
      );

      for (let j = 0; j < count; j += 1) {
        const r1 = hash(c * 7.1 + j * 2.3);
        const r2 = hash(c * 3.7 + j * 5.9);

        ctx.fillStyle = `rgba(255,255,255,${(0.2 + 0.5 * daylight) * (0.6 + 0.4 * r1)})`;
        ctx.beginPath();
        ctx.arc(
          x + (r1 - 0.5) * 16,
          y + r2 * 5,
          1 + (0.6 + whitecapIntensity) * r2 * 2.4,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
    });
  }

  /**
   * Foam blooms at breaking crests, drifts back and dissolves. Each patch
   * re-seats itself on the live surface every frame (a free lookup into the
   * surface polyline), so foam rides the wave instead of hanging where the crest
   * used to be.
   */
  private drawAnimatedWhitecaps(
    { whitecapIntensity, daylight, dt, surfaceYAt, foamMax }: FoamFrame,
    crests: Crest[],
  ): void {
    const { ctx } = this;

    crests.forEach(([x, , localSteepness]) => {
      if (
        Math.random() <
        whitecapIntensity * (0.2 + 0.8 * localSteepness) * 0.45
      ) {
        const dabs = 1 + Math.round(whitecapIntensity * 3);

        for (let j = 0; j < dabs; j += 1) {
          this.foam.push({
            x: x + (Math.random() - 0.5) * 14,
            dy: Math.random() * 3.5,
            radius: 1 + Math.random() * 2.4 * (0.6 + whitecapIntensity),
            life: 0.3 + Math.random() * 0.45,
          });
        }
      }
    });

    const buckets: FoamPatch[][] = [[], [], []];
    const baseAlpha = 0.35 + 0.45 * daylight;
    const bucketAlpha = [0.18, 0.42, 0.72];

    for (let i = this.foam.length - 1; i >= 0; i -= 1) {
      const patch = this.foam[i];
      patch.life -= dt;

      if (patch.life <= 0) {
        this.foam.splice(i, 1);
        continue;
      }

      patch.x -= FOAM_DRIFT_PX_S * dt; // left behind as the crest moves on

      const fade = Math.min(0.9, patch.life * 2.2);
      buckets[fade < 0.3 ? 0 : fade < 0.6 ? 1 : 2].push(patch);
    }

    buckets.forEach((bucket, b) => {
      if (bucket.length === 0) {
        return;
      }

      ctx.fillStyle = `rgba(255,255,255,${(baseAlpha * bucketAlpha[b]).toFixed(3)})`;
      ctx.beginPath();

      bucket.forEach((patch) => {
        const y = surfaceYAt(patch.x) + patch.dy;
        ctx.moveTo(patch.x + patch.radius, y);
        ctx.arc(patch.x, y, patch.radius, 0, Math.PI * 2);
      });

      ctx.fill();
    });

    if (this.foam.length > foamMax) {
      this.foam.splice(0, this.foam.length - foamMax);
    }
  }

  /** Above about F6 the wind tears spray off the breaking crests and blows it downwind. */
  private drawSpindrift(
    { windSpeedMps, daylight, dt, spindriftMax }: FoamFrame,
    crests: Crest[],
  ): void {
    const { ctx } = this;
    const intensity = smoothstep(13, 18, windSpeedMps);

    if (intensity > 0.002 && spindriftMax > 0) {
      crests.forEach(([x, y, localSteepness]) => {
        if (Math.random() < intensity * (0.25 + 0.75 * localSteepness) * 0.7) {
          this.spindrift.push({
            x,
            y: y - 1,
            vx: -(55 + intensity * 150) * (0.7 + Math.random() * 0.6),
            vy: -(12 + Math.random() * 32),
            life: 0.4 + Math.random() * 0.5,
          });
        }
      });
    }

    const buckets: Particle[][] = [[], []];
    const baseAlpha = 0.18 + 0.3 * daylight;
    const bucketAlpha = [0.18, 0.48];

    for (let i = this.spindrift.length - 1; i >= 0; i -= 1) {
      const p = this.spindrift[i];
      p.life -= dt;

      if (p.life <= 0) {
        this.spindrift.splice(i, 1);
        continue;
      }

      // light gravity, so it hangs and blows rather than falling straight back
      p.vy += SPINDRIFT_GRAVITY * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;

      buckets[Math.min(0.6, p.life * 1.6) < 0.3 ? 0 : 1].push(p);
    }

    FoamRenderer.fillParticleBuckets(
      ctx,
      buckets,
      baseAlpha,
      bucketAlpha,
      1.0,
      1.6,
    );

    if (this.spindrift.length > spindriftMax) {
      this.spindrift.splice(0, this.spindrift.length - spindriftMax);
    }
  }

  /**
   * Slam spray, thrown when the bow closes hard on the water, plus the burst at
   * the instant the hull goes over.
   *
   * Returns the bow clearance so the caller can carry it to the next frame.
   */
  drawSpray(frame: SprayFrame): number {
    const {
      centreX,
      baseY,
      hullPx,
      pitchRad,
      heavePx,
      capsize,
      previousCapsize,
      previousGapPx,
      elevationAt,
      clampPx,
      daylight,
      dt,
      particleMax,
    } = frame;

    const { ctx } = this;

    if (capsize > 0.5 && previousCapsize <= 0.5) {
      for (let j = 0; j < CAPSIZE_SPLASH_COUNT; j += 1) {
        this.particles.push({
          x: centreX + (Math.random() - 0.5) * hullPx * 0.85,
          y: baseY - heavePx,
          vx: (Math.random() - 0.5) * 150,
          vy: -(60 + Math.random() * 170),
          life: 0.5 + Math.random() * 0.5,
        });
      }
    }

    const bowX = centreX + Math.cos(pitchRad) * hullPx * 0.5;
    const bowY = baseY - heavePx - Math.sin(-pitchRad) * hullPx * 0.5;
    const waterY =
      baseY - Math.max(-clampPx, Math.min(clampPx, elevationAt(bowX)));
    const gap = bowY - waterY;
    const closing = (previousGapPx - gap) / Math.max(dt, 1e-3);

    // No bow slam while the hull is rolling over — the splash burst covers that.
    if (gap < SLAM_GAP_PX && closing > SLAM_CLOSING_PX_S && capsize < 0.4) {
      const intensity = Math.min(1, (closing - SLAM_CLOSING_PX_S) / 420);
      const count = Math.round(3 + intensity * 9);

      for (let j = 0; j < count; j += 1) {
        this.particles.push({
          x: bowX + (Math.random() - 0.3) * 10,
          y: waterY - 2,
          vx: (0.4 + Math.random() * 1.5) * 60,
          vy: -(70 + Math.random() * 150) * (0.5 + intensity),
          life: 0.5 + Math.random() * 0.4,
        });
      }
    }

    const buckets: Particle[][] = [[], [], []];
    const baseAlpha = 0.4 + 0.6 * daylight;
    const bucketAlpha = [0.16, 0.44, 0.72];

    for (let i = this.particles.length - 1; i >= 0; i -= 1) {
      const p = this.particles[i];
      p.life -= dt;

      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }

      p.vy += SPRAY_GRAVITY * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;

      const fade = Math.max(0, Math.min(0.85, p.life * 1.6));
      buckets[fade < 0.3 ? 0 : fade < 0.6 ? 1 : 2].push(p);
    }

    FoamRenderer.fillParticleBuckets(
      ctx,
      buckets,
      baseAlpha,
      bucketAlpha,
      1.2,
      1.6,
    );

    if (this.particles.length > particleMax) {
      this.particles.splice(0, this.particles.length - particleMax);
    }

    return gap;
  }

  private static fillParticleBuckets(
    ctx: CanvasRenderingContext2D,
    buckets: Particle[][],
    baseAlpha: number,
    bucketAlpha: number[],
    radiusBase: number,
    radiusPerLife: number,
  ): void {
    buckets.forEach((bucket, b) => {
      if (bucket.length === 0) {
        return;
      }

      ctx.fillStyle = `rgba(255,255,255,${(baseAlpha * bucketAlpha[b]).toFixed(3)})`;
      ctx.beginPath();

      bucket.forEach((p) => {
        const radius = radiusBase + p.life * radiusPerLife;
        ctx.moveTo(p.x + radius, p.y);
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      });

      ctx.fill();
    });
  }

  /** Drop every transient, so switching location does not carry foam across. */
  reset(): void {
    this.foam.length = 0;
    this.spindrift.length = 0;
    this.particles.length = 0;
  }
}
